import {
  Typography,
  Stack,
  Box,
  Button,
  Card,
  CardContent,
  InputAdornment,
  SvgIcon,
  Checkbox,
} from "@mui/material";
import { useState } from "react";
import { logOutAction } from "redux/authSlice";
import history from "utils/history";
import { LoginFields } from "models/interfaces";
import {
  handleErrorMessage,
  isTruthy,
  openSuccessNotification,
} from "helpers/methods";
import { CustomInput } from "global/components";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import viewpaths from "global/constants/ViewPathConstants";
import AuthLayout from "layouts/AuthLayout/AuthLayout";
import { useAppDispatch } from "hooks/use-app-dispatch";
import { useAppSelector } from "hooks/use-app-select";
import { expiryUpdatePwd, notExpiryUpdatePwd } from "screens/Configurations/ConfigurationsService";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import inkfreeLogo from "assets/images/side_nav_inkfree_logo.svg";
import { focusRing, mobile, signer, signerAccentBar } from "theme/colors";
import { authRef, refFont } from "./AuthReferenceStyles";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";

interface StrengthResult {
  score: number;
  label: string;
  color: string;
  checks: {
    minLength: boolean;
    hasLower: boolean;
    hasUpper: boolean;
    hasNumberOrSymbol: boolean;
  };
}

const getPasswordStrength = (password: string): StrengthResult => {
  const checks = {
    minLength: password.length >= 8,
    hasLower: /[a-z]/.test(password),
    hasUpper: /[A-Z]/.test(password),
    hasNumberOrSymbol: /[\d\W]/.test(password),
  };

  const score = Object.values(checks).filter(Boolean).length;

  const strengthMap: Record<number, { label: string; color: string }> = {
    0: { label: "Weak", color: "#e5e7eb" },
    1: { label: "Weak", color: "#e11d48" },
    2: { label: "Fair", color: "#f59e0b" },
    3: { label: "Good", color: "#3b82f6" },
    4: { label: "Strong", color: "#10b981" },
  };

  return { score, checks, ...strengthMap[score] };
};

const ExpiryPassword = () => {
  useTitle(title.PASSWORD_EXPIRED);
  const dispatch = useAppDispatch();
  const { email, pwdExpiryEnable, pwdExpiryRemainingDays } = useAppSelector(
    (state) => state.auth,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [pwsFields, setPwdFields] = useState<LoginFields>({
    oldPwd: { value: "", error: "" },
    pwd: { value: "", error: "" },
    rePassword: { value: "", error: "" },
  });

  // Single toggle — controls all three password fields at once
    const [showOldPwd, setShowOldPwd] = useState<boolean>(false);
  const [showNewPwd, setShowNewPwd] = useState<boolean>(false);
  const [showRePassword, setShowRePassword] = useState<boolean>(false);
  const [signOutOtherDevices, setSignOutOtherDevices] = useState<boolean>(false);
  // Reference 3h is the heads-up card only; the form is the second step,
  // revealed by "Update it now".
  const [showForm, setShowForm] = useState<boolean>(false);

  const strength = getPasswordStrength(pwsFields.pwd.value);

  const handleSubmit = async () => {
    try {
      const body: any = pswValidation();
      if (body) {
        setLoading(true);
        if (pwdExpiryRemainingDays <= 0) {
          await expiryUpdatePwd({
            email: email,
            pwd: body.pwd,
            oldPwd: body.oldPwd,
          }, signOutOtherDevices, true);
        } else {
          await notExpiryUpdatePwd({
            email: email,
            pwd: body.pwd,
            oldPwd: body.oldPwd,
          }, signOutOtherDevices);
        }
        openSuccessNotification("Successfully updated password.");
        dispatch(logOutAction(null));
        history.push(viewpaths.landingViewPath);
      }
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  // Single source of truth for all field validation
  const validateField = (
    name: string,
    value: string,
    allValues: { oldPwd: string; pwd: string; rePassword: string },
  ): string => {
    switch (name) {
      case "oldPwd":
        if (!value) return "Please enter old password.";
        if (value.length < 8) return "Password must be 8 characters long";
        return "";

      case "pwd":
        if (!value) return "Please enter new password.";
        return "";

      case "rePassword":
        if (!value) return "Please re-enter new password.";
        if (value !== allValues.pwd) return "Passwords don't match!";
        return "";

      default:
        return "";
    }
  };

  const handleOnChange = (e: any) => {
    const { name, value } = e.target;

    const allValues = {
      oldPwd: name === "oldPwd" ? value : pwsFields.oldPwd.value,
      pwd: name === "pwd" ? value : pwsFields.pwd.value,
      rePassword: name === "rePassword" ? value : pwsFields.rePassword.value,
    };

    const error = validateField(name, value, allValues);

    // When pwd changes, also re-validate rePassword live
    if (name === "pwd" && pwsFields.rePassword.value) {
      const rePasswordError = validateField(
        "rePassword",
        pwsFields.rePassword.value,
        allValues,
      );
      setPwdFields((prev) => ({
        ...prev,
        [name]: { value, error },
        rePassword: { ...prev.rePassword, error: rePasswordError },
      }));
      return;
    }

    setPwdFields((prev) => ({
      ...prev,
      [name]: { ...prev[name], value, error },
    }));
  };

  const pswValidation = () => {
    const allValues = {
      oldPwd: pwsFields.oldPwd.value,
      pwd: pwsFields.pwd.value,
      rePassword: pwsFields.rePassword.value,
    };

    const errors = {
      oldPwd: validateField("oldPwd", allValues.oldPwd, allValues),
      pwd: validateField("pwd", allValues.pwd, allValues),
      rePassword: validateField("rePassword", allValues.rePassword, allValues),
    };

    const hasErrors = Object.values(errors).some((e) => isTruthy(e));

    if (hasErrors) {
      setPwdFields((prev) => ({
        ...prev,
        oldPwd: { ...prev.oldPwd, error: errors.oldPwd },
        pwd: { ...prev.pwd, error: errors.pwd },
        rePassword: { ...prev.rePassword, error: errors.rePassword },
      }));
      return false;
    }

    return {
      pwd: allValues.pwd,
      rePassword: allValues.rePassword,
      oldPwd: allValues.oldPwd,
    };
  };

    const isFormValid = (): boolean => {
  const allValues = {
    oldPwd: pwsFields.oldPwd.value,
    pwd: pwsFields.pwd.value,
    rePassword: pwsFields.rePassword.value,
  };

  const hasErrors =
    !!validateField("oldPwd", allValues.oldPwd, allValues) ||
    !!validateField("pwd", allValues.pwd, allValues) ||
    !!validateField("rePassword", allValues.rePassword, allValues);

  return !hasErrors && strength.score === 4;
};

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const eyeAdornment = (
    visible: boolean,
    toggle: () => void,
    ariaLabel: string,
  ) => ({
    endAdornment: (
      <InputAdornment
        position="end"
        onClick={toggle}
        onKeyPress={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        sx={{
          cursor: "pointer",
          "&:focus-visible": {
            outline: `2px solid ${focusRing}`,
            outlineOffset: "2px",
          },
        }}
      >
        <SvgIcon>
          {visible ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
        </SvgIcon>
      </InputAdornment>
    ),
  });

const sessionCheckboxUI = () => (
  <Stack spacing={1} sx={{ pt: 1 }}>
    {/* Label row — same as ChangePassword sessionSettingsUI */}
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <SecurityOutlinedIcon sx={{ fontSize: "16px", color: "text.secondary" }} />
      <Typography sx={{ fontSize: "13px", color: "text.primary" }}>
        After updating, what should happen to your other devices?
      </Typography>
    </Stack>

    {/* Checkbox */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        borderRadius: "8px",
        cursor: "pointer",
        transition: "all 0.15s ease",
      }}
      onClick={() => setSignOutOtherDevices((v) => !v)}
    >
      <Checkbox
        checked={signOutOtherDevices}
        onChange={(e) => {
          e.stopPropagation();
          setSignOutOtherDevices(e.target.checked);
        }}
        inputProps={{ "aria-label": "Sign out other devices" }}
        disableRipple
        size="small"
        sx={{
          padding: 0,
          flexShrink: 0,
          color: "action.disabled",
          "&.Mui-checked": { color: "primary.main" },
          "& .MuiSvgIcon-root": { fontSize: "18px" },
        }}
      />
      <Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "primary.main" }}>
          Sign out other devices
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          All other active sessions will be logged out immediately.
        </Typography>
      </Box>
    </Box>
  </Stack>
);

// Segment bar + pill checklist, matching reference 3e (and NewPassword). The
// graded strength colour is kept — it carries more signal than 3e's single
// green state, which only ever renders "Strong".
const passwordStrengthUI = () => {
  const criteriaList = [
    { key: "minLength", label: "8+ characters" },
    { key: "hasNumberOrSymbol", label: "A number or symbol" },
    { key: "hasUpper", label: "Uppercase letter" },
    { key: "hasLower", label: "Lowercase letter" },
  ] as const;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
        <Box sx={{ flex: 1, display: "flex", gap: 0.625 }}>
          {[1, 2, 3, 4].map((seg) => (
            <Box
              key={seg}
              sx={{
                flex: 1,
                height: 5,
                borderRadius: "999px",
                backgroundColor:
                  seg <= strength.score ? strength.color : signer.border.light,
                transition: "background-color 0.3s ease",
              }}
            />
          ))}
        </Box>
        <Typography
          sx={{
            fontSize: refFont.cardMeta,
            fontWeight: 700,
            color: strength.color,
          }}
        >
          {pwsFields.pwd.value ? strength.label : ""}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {criteriaList.map(({ key, label }) => {
          const passed = strength.checks[key];
          return (
            <Box
              key={key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.625,
                px: "10px",
                py: "4px",
                borderRadius: "999px",
                fontSize: refFont.legal,
                fontWeight: 600,
                color: passed ? authRef.successBadgeIcon : authRef.metaText,
                backgroundColor: passed
                  ? authRef.successBadgeBg
                  : signer.border.light,
                transition: "all 0.2s",
              }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M4.5 12.5l5 5L20 6.5" />
              </svg>
              {label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

  const passwordFields = () => {
    // All three fields share the same type driven by showAllPasswords

    return (
      <Stack direction={"column"} spacing={2} width={"100%"}>
        <CustomInput
          label="Current Password"
        //   labelSx={{ color: "#A6A6A6" }}
          placeHolder={"Enter current password"}
          value={pwsFields.oldPwd.value}
          onChange={handleOnChange}
          onKeyPress={handleKeypress}
          type={showOldPwd ? "text" : "password"}
          name="oldPwd"
          required={true}
          id="oldPassword"
          propsToInputElement={{ maxLength: 30 }}
          error={!!pwsFields.oldPwd.error}
          helperText={pwsFields.oldPwd.error}
          InputProps={eyeAdornment(
            showOldPwd,
            () => setShowOldPwd((v) => !v),
            showOldPwd ? "Hide old password" : "Show old password",
          )}
        />

        <CustomInput
          label="New Password"
        //   labelSx={{ color: "#A6A6A6" }}
          placeHolder={"Enter new password"}
          value={pwsFields.pwd.value}
          onChange={handleOnChange}
          onKeyPress={handleKeypress}
          type={showNewPwd ? "text" : "password"}
          name="pwd"
          required={true}
          id="newpassword"
          propsToInputElement={{ maxLength: 30 }}
          InputProps={eyeAdornment(
            showNewPwd,
            () => setShowNewPwd((v) => !v),
            showNewPwd ? "Hide new password" : "Show new password",
          )}
        />

        {/* Strength checker — shown while typing new password */}
        {passwordStrengthUI()}

        <CustomInput
          label="Re-Enter New Password"
        //   labelSx={{ color: "#A6A6A6" }}
          placeHolder="Enter new Password again"
          value={pwsFields.rePassword.value}
          onChange={handleOnChange}
          onKeyPress={handleKeypress}
          type={showRePassword ? "text" : "password"}
          name="rePassword"
          error={!!pwsFields.rePassword.error}
          helperText={pwsFields.rePassword.error}
          required={true}
          id="repassword"
          propsToInputElement={{ maxLength: 30 }}
          InputProps={eyeAdornment(
            showRePassword,
            () => setShowRePassword((v) => !v),
            showRePassword ? "Hide confirm password" : "Show confirm password",
          )}
        />

        {sessionCheckboxUI()}
      </Stack>
    );
  };

  const isWarning = pwdExpiryEnable === true && pwdExpiryRemainingDays > 0;

  // The reference shows "85 / 90 days" against the full policy period, but the
  // only expiry data reaching this screen is the remaining-day count. The app's
  // own heads-up window is 7 days (shouldShowPasswordExpiryPage in Login), so
  // the meter is drawn against that rather than inventing a policy length.
  const graceWindowDays = 7;
  const elapsedShare = Math.min(
    100,
    Math.max(
      0,
      ((graceWindowDays - pwdExpiryRemainingDays) / graceWindowDays) * 100,
    ),
  );
  const expiresOn = new Date(
    Date.now() + pwdExpiryRemainingDays * 24 * 60 * 60 * 1000,
  ).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const secondaryButtonSx = {
    mt: 1.25,
    height: "46px",
    borderRadius: "8px",
    borderColor: authRef.inputBorder,
    color: authRef.subText,
    backgroundColor: "background.paper",
    fontWeight: 600,
    fontSize: refFont.cardBody,
    textTransform: "none",
    "&:hover": {
      borderColor: authRef.inputBorderHover,
      backgroundColor: "background.paper",
    },
  } as const;

  // Step 1 — the heads-up card, per reference 3h. No form until "Update it now".
  const renderNoticeStep = () => (
    <>
      <Box
        aria-hidden="true"
        sx={{
          width: 64,
          height: 64,
          mt: 3,
          borderRadius: "50%",
          backgroundColor: authRef.warnTint,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke={signer.warn.main}
          strokeWidth="1.8"
          style={{ width: 28, height: 28 }}
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3.2 2" />
        </svg>
      </Box>

      <Typography
        component="h1"
        sx={{
          mt: 2,
          color: mobile.base[500],
          fontSize: refFont.cardHeading,
          fontWeight: 700,
          textAlign: "center",
        }}
      >
        {isWarning ? (
          <>
            Your password retires in{" "}
            <Box component="span" sx={{ color: signer.orange.main }}>
              {pwdExpiryRemainingDays} day
              {pwdExpiryRemainingDays > 1 ? "s" : ""}
            </Box>
          </>
        ) : (
          "Your password has expired"
        )}
      </Typography>

      <Typography
        sx={{
          mt: 1.25,
          color: authRef.subText,
          fontSize: refFont.cardBody,
          lineHeight: 1.65,
          textAlign: "center",
        }}
      >
        {isWarning ? (
          <>
            Company policy asks for a fresh password periodically.
            <br />
            Yours expires on{" "}
            <Box
              component="span"
              sx={{ fontWeight: 700, color: mobile.base[500] }}
            >
              {expiresOn}
            </Box>
            .
          </>
        ) : (
          "Set a new password to continue using your account."
        )}
      </Typography>

      {isWarning && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            mt: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: refFont.cardFooter,
              color: authRef.metaText,
            }}
          >
            <Box component="span">Time remaining</Box>
            <Box
              component="span"
              sx={{ fontWeight: 700, color: signer.warn.main }}
            >
              {pwdExpiryRemainingDays} day
              {pwdExpiryRemainingDays > 1 ? "s" : ""} left
            </Box>
          </Box>
          <Box
            sx={{
              height: 6,
              borderRadius: "999px",
              backgroundColor: signer.border.light,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${elapsedShare}%`,
                height: "100%",
                borderRadius: "999px",
                background: `linear-gradient(90deg, ${signer.gradient.amber}, ${signer.gradient.coral})`,
              }}
            />
          </Box>
        </Box>
      )}

      <Button
        fullWidth
        variant="contained"
        onClick={() => setShowForm(true)}
        sx={{
          mt: 3,
          height: "48px",
          borderRadius: "8px",
          fontWeight: 700,
          fontSize: refFont.cardButton,
          letterSpacing: 0,
          textTransform: "none",
        }}
      >
        Update it now
      </Button>

      {isWarning && (
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            localStorage.setItem("skipPasswordExpiry", "true");
            history.push(viewpaths.dashboardViewPath);
          }}
          sx={secondaryButtonSx}
        >
          Remind me later
        </Button>
      )}

      {pwdExpiryEnable === true && pwdExpiryRemainingDays <= 0 && (
        <Button
          fullWidth
          variant="outlined"
          type="button"
          onClick={() => {
            dispatch(logOutAction(null));
            history.push(viewpaths.landingViewPath);
          }}
          sx={secondaryButtonSx}
        >
          Cancel
        </Button>
      )}

      {isWarning && (
        <Typography
          sx={{
            mt: 2,
            color: authRef.metaText,
            fontSize: refFont.cardFooter,
            textAlign: "center",
          }}
        >
          We'll nudge you daily until it's updated.
        </Typography>
      )}
    </>
  );

  // Step 2 — the change-password form, dressed as reference 3e.
  const renderPasswordStep = () => (
    <>
      <Typography
        component="h1"
        sx={{
          mt: 3,
          color: mobile.base[500],
          fontSize: refFont.cardHeading,
          fontWeight: 700,
          textAlign: "center",
        }}
      >
        Set your new password
      </Typography>
      <Typography
        sx={{
          mt: 1,
          color: authRef.subText,
          fontSize: refFont.cardBody,
          lineHeight: 1.6,
          textAlign: "center",
        }}
      >
        Make it strong — it guards your signatures.
      </Typography>

      <Box
        sx={{
          width: "100%",
          mt: 3.25,
          // Field chrome per reference 3e: 46px tall, 8px radius, pale fill,
          // 14px inset. `&&` outranks CustomInput's !important.
          "&& .MuiOutlinedInput-root": {
            minHeight: "46px",
            height: "46px",
            borderRadius: "8px",
            padding: "0 14px !important",
            backgroundColor: authRef.inputBg,
            fontSize: refFont.body,
            fontWeight: 500,
            color: mobile.base[500],
            "& fieldset": { borderColor: authRef.inputBorder },
            "&:hover fieldset": { borderColor: authRef.inputBorderHover },
          },
          "&& .MuiInputBase-input": { padding: "0 !important" },
          "&& .MuiInputBase-input::placeholder": {
            color: authRef.metaText,
            opacity: 1,
          },
          "&& .MuiInputLabel-root": {
            fontSize: refFont.label,
            fontWeight: 600,
            color: mobile.base[500],
            marginLeft: 0,
            "& .MuiFormLabel-asterisk": { color: signer.orange.main },
          },
        }}
      >
        {passwordFields()}
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={() => handleSubmit()}
        disabled={loading || !isFormValid()}
        sx={{
          mt: 3,
          height: "48px",
          borderRadius: "8px",
          fontWeight: 700,
          fontSize: refFont.cardButton,
          letterSpacing: 0,
          textTransform: "none",
        }}
      >
        {loading ? "Updating..." : "Update password"}
      </Button>

      <Button
        fullWidth
        variant="outlined"
        type="button"
        onClick={() => setShowForm(false)}
        disabled={loading}
        sx={secondaryButtonSx}
      >
        Back
      </Button>
    </>
  );

  return (
    <AuthLayout>
      {/* Brand accent bar across the top of the canvas, per reference 3h. */}
      <Box
        aria-hidden="true"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "5px",
          background: signerAccentBar,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      />
      <Card
        elevation={0}
        id="credential-component-container"
        sx={{
          width: "100%",
          // 470px per reference 3h/3e — the extra width was only there to fit
          // the old two-column strength checklist.
          maxWidth: 470,
          mx: "auto",
          borderRadius: "16px",
          border: `1px solid ${authRef.cardBorder}`,
          boxShadow: authRef.cardShadow,
          padding: { xs: "28px 24px 24px", sm: "40px 44px 36px" },
          boxSizing: "border-box",
        }}
      >
        <CardContent
          sx={{
            p: 0,
            "&:last-child": { pb: 0 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={inkfreeLogo}
            alt="Inkfree e-Sign"
            sx={{ width: 106, height: "auto", display: "block" }}
          />

          {showForm ? renderPasswordStep() : renderNoticeStep()}
        </CardContent>
      </Card>
      <CustomLoader isLoading={loading} />
    </AuthLayout>
  );
};

export default ExpiryPassword;
